import { useCallback } from 'react';

import { imageTracking } from 'src/modules/homeownerApp/components/Customize/tracking';
import { useGetProjectsQuery } from 'src/shared/graphql/generated';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { buttonPressed } from 'src/shared/telemetry';

import { imageIsAddedToAnyProject } from '../components/Utils';
import { HomeownerAppMenuNames } from '../providers/HomeownerAppProvider';
import { useSaveToProjectContext } from '../providers/SaveToProjectProvider';
import type { AiOrSampleDisplayImage } from '../types';
import { isSample } from '../types';

import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseSaveToProjectOptions {
  image: AiOrSampleDisplayImage | undefined | null;
  location: string;
}

export const useSaveToProject = ({
  image,
  location,
}: UseSaveToProjectOptions) => {
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const { data } = useGetProjectsQuery();
  const { setImageToSave } = useSaveToProjectContext();

  const isSaved =
    !isSample(image) &&
    imageIsAddedToAnyProject(
      data?.projects || [],
      image?.aiGeneratedImage?.id,
      !!image?.favoritedAt,
    );

  const handleSave = useCallback(() => {
    if (isSample(image)) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
      return;
    }

    if (!image) return;

    buttonPressed({
      ...trackingData,
      ...imageTracking(image),
      button_location: location,
      button_text: 'Save',
      partner_id: partnerId,
      primary_cta: false,
      track_id: 'save-image',
    });

    setImageToSave(image);
  }, [
    image,
    location,
    partnerId,
    setCurrentMenu,
    setImageToSave,
    trackingData,
  ]);

  return { isSaved, handleSave };
};
